import { makeAutoObservable, runInAction } from 'mobx'
import { cloneDeep, isEqual, upperFirst } from 'lodash'
import { AxiosError } from 'axios'
import { nanoid } from 'nanoid'
import {
  CountryEnum,
  Divider,
  EnumDropdownItemVariant,
  type IDropdownItem,
  PhoneInputStore,
  showToast,
  toastStore,
} from 'shared/ui'
import { getTimezonesList, logger } from 'shared/lib'
import modalStore from 'shared/ui/Modal/store/modalStore'
import { ModalTypeList } from 'shared/ui/Modal/store/types'
import { MAX_PASSWORD_LENGTH } from 'shared/constants/auth'
import { uiStore } from 'shared/store/uiStore'
import { PhoneNumberInputStore } from 'shared/ui/PhoneNumberInput/store/phoneNumberInputStore'
import type { IParamsUpdateUserProfile } from 'entities/Users/api/types'
import type { UserOwner } from 'entities/Users/model/UserOwner'
import { UsersApi, type UsersStore } from 'entities/Users'
import type { IEntitiesStore } from 'entities/EntitiesExport'
import type { IProfileFormFields } from 'pages/settings/pages/personal/pages/account/store/type'
import { CheckCurrentPasswordModal } from 'pages/settings/pages/personal/pages/account/pages/profile/ui/CheckCurrentPasswordModal/CheckCurrentPasswordModal'
import { ChangeEmailModal } from 'pages/settings/pages/personal/pages/account/pages/profile/ui/ChangeEmailModal/ChangeEmailModal'
import { ChangePhoneModal } from 'pages/settings/pages/personal/pages/account/pages/profile/ui/ChangePhoneModal/ChangePhoneModal'
import { CheckCurrentPasswordModalActions } from 'pages/settings/pages/personal/pages/account/pages/profile/ui/CheckCurrentPasswordModal/CheckCurrentPasswordModalActions'
import { ChangeEmailModalActions } from 'pages/settings/pages/personal/pages/account/pages/profile/ui/ChangeEmailModal/ChangeEmailModalActions'
import { ChangePhoneModalActions } from 'pages/settings/pages/personal/pages/account/pages/profile/ui/ChangePhoneModal/ChangePhoneModalActions'
import { ChangePasswordModal } from 'pages/settings/pages/personal/pages/account/pages/profile/ui/ChangePasswordModal/ChangePasswordModal'
import { ChangePasswordModalActions } from 'pages/settings/pages/personal/pages/account/pages/profile/ui/ChangePasswordModal/ChangePasswordModalActions'

export class ProfileStore {
  private _photoUrl = ''
  private _firstName = ''
  private _lastName = ''
  private _number = ''
  private _email = ''
  private _newEmail = ''
  private _timezone = ''
  private readonly _phoneNumberInputStore
  private readonly _phoneNewInputStore
  private readonly _usersStore: UsersStore
  private _limitFirstNameReached = false
  private _limitLastNameReached = false
  private _loading = false
  private _errorsMap = new Map<IProfileFormFields, string>()
  private _updatedFields: Set<string> = new Set()
  private _savedParams: IParamsUpdateUserProfile | null = null
  private _currentPasswordConfirmed = false
  private _currentPassword = ''
  private _newPassword = ''
  private _confirmNewPassword = ''
  private _modalId = nanoid()

  constructor(private _user: UserOwner, private _entities: IEntitiesStore) {
    makeAutoObservable(this)

    this._usersStore = this._entities.usersStore
    this._phoneNumberInputStore = new PhoneNumberInputStore()
    this._phoneNewInputStore = new PhoneInputStore()
  }

  get phoneNumberInputStore() {
    return this._phoneNumberInputStore
  }

  get phoneNewInputStore() {
    return this._phoneNewInputStore
  }

  get firstName() {
    return this._firstName
  }

  get lastName() {
    return this._lastName
  }

  get timezone() {
    return this._timezone
  }

  get email() {
    return this._email
  }

  get newEmail() {
    return this._newEmail
  }

  get currentPassword() {
    return this._currentPassword
  }

  get currentPasswordConfirmed() {
    return this._currentPasswordConfirmed
  }

  get newPassword() {
    return this._newPassword
  }

  get confirmNewPassword() {
    return this._confirmNewPassword
  }

  get timezoneItems(): IDropdownItem[] {
    return getTimezonesList()
      .sort((a, b) => {
        if (!a.group && !b.group) return 0
        if (!a.group) return 1
        if (!b.group) return -1
        return a.group.localeCompare(b.group)
      })
      .map((tz) => ({
        id: tz.value,
        label: tz.label,
        value: tz.value,
        group: tz.group,
        variant: EnumDropdownItemVariant.CheckedRight,
      }))
  }

  get limitFirstNameReached() {
    return this._limitFirstNameReached
  }

  get limitLastNameReached() {
    return this._limitLastNameReached
  }

  get loading() {
    return this._loading
  }

  get params(): IParamsUpdateUserProfile {
    return {
      first_name: this._firstName.trim(),
      last_name: this._lastName.trim(),
      number: this.phoneNewInputStore.number
        ? this.phoneNewInputStore.fullUnformattedNumber
        : this._phoneNumberInputStore.number,
      photo_url: this._photoUrl,
      timezone: this._timezone,
    }
  }

  get isExistChanges() {
    return !isEqual(this._savedParams, this.params)
  }

  get errorsMap() {
    return this._errorsMap
  }

  get phoneExcludeCountries() {
    const excludedCountriesArray: CountryEnum[] = []

    switch (this._user.country) {
      case CountryEnum.US:
      case CountryEnum.CA:
        excludedCountriesArray.push(CountryEnum.GB)
        excludedCountriesArray.push(CountryEnum.AU)
        break

      case CountryEnum.GB:
        excludedCountriesArray.push(CountryEnum.US)
        excludedCountriesArray.push(CountryEnum.CA)
        excludedCountriesArray.push(CountryEnum.AU)
        break

      default:
        excludedCountriesArray.push(CountryEnum.AU)
    }

    return excludedCountriesArray
  }

  init = () => {
    this._photoUrl = this._user.photo_url
    this._firstName = this._user.first_name || ''
    this._lastName = this._user.last_name || ''
    this._timezone = this._user.timezone || ''
    this._email = this._user.email || ''
    this._number = this._user.user_number || ''

    if (this._number) {
      const origin = this._user.origin

      this.phoneNumberInputStore.setCountryById(origin.country)
      this.phoneNumberInputStore.setNumber(origin.full_number)
    }

    this._savedParams = cloneDeep(this.params)
    this.phoneNewInputStore.setExcludeCountries(this.phoneExcludeCountries)
  }

  setLoading = (loading: boolean) => {
    this._loading = loading
  }

  setError = (key: IProfileFormFields, error: string) => {
    this._errorsMap.set(key, error)
  }

  removeError = (key: IProfileFormFields) => {
    this._errorsMap.delete(key)
  }

  setLimitFirstNameReached = (status: boolean) => {
    this._limitFirstNameReached = status
  }

  setLimitLastNameReached = (status: boolean) => {
    this._limitLastNameReached = status
  }

  setTimezone = (value: string) => {
    this._timezone = value
    this.removeError('timezone')
    this._updatedFields.add('Timezone')
  }

  setEmail = () => {
    this.removeError('email')
    this.removeError('current_password')
    void this.openCheckCurrentPasswordModal('email')
  }

  setNewEmail = (email: string) => {
    this._newEmail = email
    this.removeError('email')
  }

  setNumber = () => {
    this.removeError('phone_number')
    this.removeError('current_password')
    void this.openCheckCurrentPasswordModal('phone number')
  }

  setFirstName = (value: string) => {
    this._firstName = value
    this.removeError('first_name')

    if (!isEqual(upperFirst(value.trim()), this._savedParams?.first_name)) {
      this._updatedFields.add('First name')
    }
  }

  setLastName = (value: string) => {
    this._lastName = value
    this.removeError('last_name')

    if (!isEqual(upperFirst(value.trim()), this._savedParams?.last_name)) {
      this._updatedFields.add('Last name')
    }
  }

  setCurrentPassword = (value: string) => {
    this._currentPassword = value
    this.removeError('current_password')
  }

  setNewPassword = (value: string) => {
    if (value.length > MAX_PASSWORD_LENGTH) return

    this._newPassword = value
    this.removeError('new_password')
  }

  setConfirmNewPassword = (value: string) => {
    if (value.length > MAX_PASSWORD_LENGTH) return

    this._confirmNewPassword = value
  }

  reset = () => {
    this._firstName = ''
    this._lastName = ''
    this._number = ''
    this._email = ''
    this._newEmail = ''
    this._timezone = ''
    this._updatedFields.clear()
    this._limitFirstNameReached = false
    this._limitLastNameReached = false
    this._loading = false
    this._phoneNumberInputStore.reset()
    this._phoneNewInputStore.reset()
    this._errorsMap.clear()
    this._savedParams = null
    this.resetCurrentPassword()
    this._newPassword = ''
    this._confirmNewPassword = ''
  }

  resetCurrentPassword() {
    this._currentPasswordConfirmed = false
    this._currentPassword = ''
  }

  checkValidate = () => {
    let isValid = true

    if (this._firstName.trim().length < 2) {
      this.setError('first_name', 'First name must be a minimum of 2 characters')
      isValid = false
    }

    if (!this._firstName.trim().length) {
      this.setError('first_name', 'Please enter First name')
      isValid = false
    }

    if (!this._lastName.trim().length) {
      this.setError('last_name', 'Please enter Last name')
      isValid = false
    }

    if (!this._phoneNumberInputStore.isValidNumber) {
      this.setError('phone_initial_number', 'Please enter phone number')
    }

    return isValid
  }

  refreshUser = async () => {
    try {
      this.setLoading(true)

      const { data } = await UsersApi.getUser()

      runInAction(() => {
        this._usersStore.addItem(data)
        this.init()
      })
    } catch (e) {
      logger.error(e)
    } finally {
      this.setLoading(false)
    }
  }

  onSaveProfile = async () => {
    if (this._loading || !this.checkValidate() || !this.isExistChanges) return false

    try {
      this.setLoading(true)

      const params = this.params

      await UsersApi.updateUsersProfile(params)

      Array.from(this._updatedFields).forEach((field) => {
        showToast({
          type: 'success',
          title: `${field} updated`,
        })
      })

      if (this._savedParams?.timezone !== params.timezone) uiStore.setTimezone(params.timezone)

      runInAction(() => {
        this._savedParams = cloneDeep(params)
        this._updatedFields.clear()
      })

      void this.refreshUser()
      this.removeError('phone_initial_number')

      return true
    } catch (e) {
      logger.error(e)

      if (e instanceof AxiosError) {
        if (e.errors.getError('number')) {
          const key = modalStore.hasModal(this._modalId) ? 'phone_number' : 'phone_initial_number'
          this.setError(key, e.errors.getError('number'))
        } else {
          showToast({
            type: 'error',
            title: 'Something went wrong. Please try again.',
          })
        }
      } else {
        showToast({
          type: 'error',
          title: 'Something went wrong. Please try again.',
        })
      }

      this.setLoading(false)

      return false
    }
  }

  handleTimezone = (value: string) => {
    this.setTimezone(value)
    void this.onSaveProfile()
  }

  handleLogoutAllDevices = () => {
    modalStore.addModal({
      id: this._modalId,
      type: ModalTypeList.WARNING,
      title: 'Log out all devices?',
      desc: (
        <>
          You will be logged out of all active sessions on all devices except
          <br />
          for this one
        </>
      ),
      primaryAction: {
        text: 'Log out all devices',
        onAction: async () => {
          try {
            this.setLoading(true)
            await UsersApi.logoutOtherDevices()

            toastStore.add({
              type: 'success',
              title: 'Logged out of other devices',
            })
          } catch (error) {
            logger.error(error)

            toastStore.add({
              type: 'error',
              title: 'Something went wrong. Please try again',
            })
          } finally {
            this.onCloseModal()
            this.setLoading(false)
          }
        },
      },
      secondaryAction: {
        text: 'Cancel',
        onAction: () => this.onCloseModal(),
      },
    })
  }

  handleCheckPasswordNext = async (field: string) => {
    try {
      await this.updateEmailAndPassword()

      if (this._currentPasswordConfirmed && field === 'email') this.openChangeEmailModal()
      if (this._currentPasswordConfirmed && field === 'phone number') this.openChangePhoneModal()
    } catch (e) {
      logger.error(e)
    }
  }

  openCheckCurrentPasswordModal = async (field: string) => {
    this.resetCurrentPassword()

    modalStore.addModal({
      id: this._modalId,
      type: ModalTypeList.DEFAULT,
      title: (
        <>
          Change {field}
          <Divider
            orientation='vertical'
            sx={{
              height: '8px !important',
              margin: '0 8px',
              display: 'inline-block',
              borderColor: 'var(--content-neutral-primary)',
            }}
          />
          1 of 2
        </>
      ),
      ModalContent: () => <CheckCurrentPasswordModal store={this} field={field} />,
      ModalActions: () => <CheckCurrentPasswordModalActions store={this} field={field} />,
      showCloseButton: false,
      width: 360,
    })
  }

  handleChangeEmailSave = async () => {
    try {
      if (this._currentPasswordConfirmed) {
        const isEmailSaved = await this.updateEmailAndPassword(true)

        if (isEmailSaved) {
          runInAction(() => {
            this._email = this._newEmail
            this._newEmail = ''
          })

          this.onCloseModal()
          showToast({
            type: 'success',
            title: 'Email updated',
          })
        }
      }
    } catch (e) {
      logger.error(e)
    }
  }

  openChangeEmailModal = () => {
    this.setNewEmail('')

    modalStore.addModal({
      id: this._modalId,
      type: ModalTypeList.DEFAULT,
      title: (
        <>
          Change email
          <Divider
            orientation='vertical'
            sx={{
              height: '8px !important',
              margin: '0 8px',
              display: 'inline-block',
              borderColor: 'var(--content-neutral-primary)',
            }}
          />
          2 of 2
        </>
      ),
      ModalContent: () => <ChangeEmailModal store={this} />,
      ModalActions: () => <ChangeEmailModalActions store={this} />,
      showCloseButton: false,
      width: 360,
    })
  }

  get isPhoneNumberNotAllowedToSave() {
    return (
      !this._phoneNumberInputStore.isValidNumber &&
      (this._firstName.trim().length < 2 || !this._lastName.trim().length)
    )
  }

  handleChangePhoneSave = async () => {
    try {
      if (!this._currentPasswordConfirmed) return

      if (this.isPhoneNumberNotAllowedToSave) {
        this.setPhoneToProfile()
        this.onCloseModal()
        this.removeError('phone_initial_number')
        this._updatedFields.add('Phone number')
        this.checkValidate()
      } else {
        const isPhoneSaved = await this.onSaveProfile()

        if (isPhoneSaved) {
          this.setPhoneToProfile()
          this.onCloseModal()
          showToast({
            type: 'success',
            title: 'Phone number updated',
          })
        }
      }
    } catch (e) {
      logger.error(e)
    }
  }

  setPhoneToProfile() {
    this.phoneNumberInputStore.setCountryById(String(this._phoneNewInputStore.country.id))
    this.phoneNumberInputStore.setNumber(this._phoneNewInputStore.fullUnformattedNumber)
    this._number = this._phoneNewInputStore.fullUnformattedNumber
    this._phoneNewInputStore.reset()
  }

  openChangePhoneModal = () => {
    this._phoneNewInputStore.reset()

    modalStore.addModal({
      id: this._modalId,
      type: ModalTypeList.DEFAULT,
      title: (
        <>
          Change phone number
          <Divider
            orientation='vertical'
            sx={{
              height: '8px !important',
              margin: '0 8px',
              display: 'inline-block',
              borderColor: 'var(--content-neutral-primary)',
            }}
          />
          2 of 2
        </>
      ),
      ModalContent: () => <ChangePhoneModal store={this} />,
      ModalActions: () => <ChangePhoneModalActions store={this} />,
      showCloseButton: false,
      width: 360,
    })
  }

  handleChangePasswordSave = async () => {
    try {
      if (this._loading) return

      this._errorsMap.clear()
      this.setLoading(true)
      await UsersApi.updatePassword({
        current_password: this._currentPassword,
        password: this._newPassword,
        password_confirmation: this._confirmNewPassword,
      })

      runInAction(() => {
        modalStore.removeModal(this._modalId)
        this.resetCurrentPassword()
        this._newPassword = ''
        this._confirmNewPassword = ''
      })

      showToast({
        type: 'success',
        title: 'Password updated',
      })
    } catch (e) {
      logger.error(e)

      if (e instanceof AxiosError) {
        if (e.errors.getError('current_password'))
          this.setError('current_password', e.errors.getError('current_password'))
        if (e.errors.getError('password'))
          this.setError('new_password', e.errors.getError('password'))
      } else {
        showToast({
          type: 'error',
          title: 'Something went wrong. Please try again.',
        })
      }
    } finally {
      this.setLoading(false)
    }
  }

  openChangePasswordModal = () => {
    this._errorsMap.clear()

    modalStore.addModal({
      id: this._modalId,
      type: ModalTypeList.DEFAULT,
      title: 'Change your password',
      ModalContent: () => <ChangePasswordModal store={this} />,
      ModalActions: () => <ChangePasswordModalActions store={this} />,
      showCloseButton: false,
      width: 360,
      onClose: () => {
        modalStore.removeModal(this._modalId)
        this.resetCurrentPassword()
        this._newPassword = ''
        this._confirmNewPassword = ''
      },
    })
  }

  onCloseModal = () => {
    modalStore.closeModal(this._modalId)
  }

  updateEmailAndPassword = async (isNewEmail = false) => {
    try {
      if (this._loading) return false

      this.setLoading(true)
      await UsersApi.updateEmail({
        current_password: this._currentPassword,
        email: isNewEmail ? this._newEmail : this._email,
      })

      runInAction(() => {
        this._currentPasswordConfirmed = true
      })

      if (isNewEmail) void this.refreshUser()

      return true
    } catch (e) {
      logger.error(e)

      if (e instanceof AxiosError) {
        if (e.errors.getError('current_password')) {
          runInAction(() => {
            this._currentPasswordConfirmed = false
          })

          this.setError('current_password', e.errors.getError('current_password'))
        }

        if (e.errors.getError('email')) this.setError('email', e.errors.getError('email'))
      }

      return false
    } finally {
      this.setLoading(false)
    }
  }

  updatePhotoUrl = (url: string) => {
    this._photoUrl = url
  }

  isFieldChanged = (fieldName: keyof IParamsUpdateUserProfile, value: string) => {
    return !!this._savedParams && !isEqual(this._savedParams[fieldName], value.trim())
  }
}
